import { ComponentType, lazy, Suspense, useCallback, useMemo, useState } from 'react';

import { logger } from '@parrot-software/observability';
import { useLocation } from 'react-router-dom';

import { createMfeError, errorKind, isRetryableRemoteError, MFE_ERROR_NAMES, toError } from './errors';
import { getMfeHost, registerRemote } from './mfeHost';
import { RemoteErrorBoundary } from './RemoteErrorBoundary';
import { RemoteConfig } from './remoteUrl';

export const DEFAULT_REMOTE_TIMEOUT_MS = 15000;

/** Props of the view shown when a remote fails to load or to render. */
export type RemoteFallbackProps = {
  error: Error;
  /** Absent when the failure cannot be fixed by trying again (remote disabled or not allowed). */
  onRetry?: () => void;
};

type CreateRemoteComponentOptions = {
  /** View rendered when the remote cannot be loaded or crashes while rendering. */
  fallback: ComponentType<RemoteFallbackProps>;
  /** View rendered while the remote loads. */
  loading: ComponentType;
  /** Exposed module to load, without the remote prefix (e.g. `App`). */
  module: string;
  remote: RemoteConfig;
  timeoutMs?: number;
};

const withTimeout = <T,>(promise: Promise<T>, timeoutMs: number, moduleId: string): Promise<T> => {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(
      () => reject(createMfeError(MFE_ERROR_NAMES.TIMEOUT, `Remote "${moduleId}" did not load in ${timeoutMs}ms`)),
      timeoutMs,
    );
  });
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
};

/**
 * Creates a React component backed by a Module Federation remote. It shows `loading` while the
 * remote loads, times out after `timeoutMs`, and renders `fallback` both when the remote cannot be
 * loaded and when it crashes while rendering, so a broken remote never takes the shell down.
 *
 * Every retry builds a new lazy component, because React.lazy caches a rejected load forever. The
 * error view also resets when the pathname changes, so navigating away from a failure recovers.
 */
export const createRemoteComponent = <TProps extends object>({
  fallback: Fallback,
  loading: Loading,
  module,
  remote,
  timeoutMs = DEFAULT_REMOTE_TIMEOUT_MS,
}: CreateRemoteComponentOptions): ComponentType<TProps> => {
  const log = logger.forDomain(remote.domain);
  const moduleId = `${remote.name}/${module}`;
  // Load failures are already logged as mfeRemoteLoad; the boundary must not report them again.
  const loadErrors = new WeakSet<Error>();

  const createLazy = (attempt: number) =>
    lazy(async () => {
      try {
        registerRemote(remote);
        const remoteModule = await withTimeout(
          getMfeHost().loadRemote<{ default: ComponentType<TProps> }>(moduleId),
          timeoutMs,
          moduleId,
        );
        log.info('mfeRemoteLoad', { attempt, remote: moduleId });
        return { default: remoteModule?.default ?? ((remoteModule as unknown) as ComponentType<TProps>) };
      } catch (error) {
        log.error('mfeRemoteLoad', {
          attempt,
          'error.kind': errorKind(error),
          'error.message': error instanceof Error ? error.message : String(error),
          remote: moduleId,
        });
        const loadError = toError(error);
        loadErrors.add(loadError);
        throw loadError;
      }
    });

  const RemoteComponent = (props: TProps) => {
    const { pathname } = useLocation();
    const [attempt, setAttempt] = useState(1);
    // A generic TProps cannot be proven ref-compatible with the lazy wrapper's props, hence the cast.
    const LazyRemote = useMemo(() => (createLazy(attempt) as unknown) as ComponentType<TProps>, [attempt]);

    const handleError = useCallback(
      (error: Error) => {
        if (loadErrors.has(error)) {
          return;
        }
        log.error('mfeRemoteRender', {
          attempt,
          'error.kind': errorKind(error),
          'error.message': error.message,
          remote: moduleId,
        });
      },
      [attempt],
    );

    const handleReset = useCallback(() => setAttempt((current) => current + 1), []);

    return (
      <RemoteErrorBoundary
        fallback={({ error, reset }) => (
          <Fallback error={error} onRetry={isRetryableRemoteError(error) ? reset : undefined} />
        )}
        onError={handleError}
        onReset={handleReset}
        resetKey={pathname}
      >
        <Suspense fallback={<Loading />}>
          <LazyRemote {...props} />
        </Suspense>
      </RemoteErrorBoundary>
    );
  };

  return RemoteComponent;
};
